/**
 * The red allergy band (المرحلة 1.2, P01) — on a patient wherever a desk or a
 * doctor has them open: reception and the other desks (PatientWorkspace),
 * nursing, the doctor's live view and the hospital doctor's panel.
 *
 * Every allergy on record: what the institution wrote or verified, and what the
 * patient wrote in their own profile, marked «من المريض» until a desk takes it
 * into the file. Nothing on record: nothing drawn, so a screen without
 * allergies looks exactly as it did.
 *
 *   <AllergyBand fileId={patient.id} />             a hospital file
 *   <AllergyBand patientId={userId} bookingId={…} /> a patient account (the doctor)
 *   <AllergyBand allergies={[…]} />                  already loaded
 */

import { useEffect, useState } from 'react';
import { Icon } from '../../components/common';
import { patientHealthApi } from '../../api/endpoints';
import { useI18n } from '../../i18n/I18nProvider';

const ORDER = { severe: 0, moderate: 1, mild: 2 };

/** The allergies of a file or an account, loaded once per patient. */
export function useAllergies({ fileId = null, patientId = null, bookingId = null, allergies = null, refreshKey = 0 } = {}) {
  const [state, setState] = useState({ allergies: allergies || [], mode: 'warn', loaded: Boolean(allergies) });
  useEffect(() => {
    if (allergies) {
      setState({ allergies, mode: 'warn', loaded: true });
      return undefined;
    }
    if (!fileId && !patientId && !bookingId) return undefined;
    let cancelled = false;
    const load = fileId ? patientHealthApi.allergies(fileId) : patientHealthApi.forPrescription({ patientId, bookingId });
    load
      .then((answer) => !cancelled && setState({ allergies: answer.allergies || [], mode: answer.mode || 'warn', loaded: true }))
      .catch(() => !cancelled && setState((s) => ({ ...s, loaded: true })));
    return () => {
      cancelled = true;
    };
  }, [fileId, patientId, bookingId, allergies, refreshKey]);
  return state;
}

export function AllergyBand({ fileId = null, patientId = null, bookingId = null, allergies = null, compact = false, refreshKey = 0 }) {
  const { t } = useI18n();
  const state = useAllergies({ fileId, patientId, bookingId, allergies, refreshKey });
  if (!state.allergies.length) return null;
  const sorted = [...state.allergies].sort((a, b) => (ORDER[a.severity] ?? 1) - (ORDER[b.severity] ?? 1));
  return (
    <div className={`allergy-band ${compact ? 'allergy-band--compact' : ''}`} role="alert" data-testid="allergy-band">
      <span className="allergy-band__title">
        <Icon name="shield-alert" size={compact ? 14 : 16} />
        {t('allergy.band', { n: sorted.length })}
      </span>
      <ul className="allergy-band__list">
        {sorted.map((a) => (
          <li key={`${a.source}-${a.id}`} className={`allergy-band__item allergy-band__item--${a.severity}`}>
            <strong>{a.substance}</strong>
            {a.reaction && !compact && <span className="allergy-band__reaction">{a.reaction}</span>}
            <span className="allergy-band__severity">{t(`healthProfile.allergies.severity.${a.severity}`)}</span>
            {!a.verified && <span className="allergy-band__from">{t('allergy.fromPatient')}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default AllergyBand;
